/**
 * The one camera: pan, zoom and tilt over the stage, shared by the GL canvas,
 * the DOM overlay and hit-testing.
 *
 * Stage units are CSS px at zoom 1. The camera looks at the stage point
 * (x, y), which sits at the centre of the stage viewport. Tilt leans the
 * plate back about that centre line under a fixed perspective, so the top
 * rows recede. `toScreen` and `toStage` are exact inverses, and `matrix`
 * produces the same projection for the GPU: a drop is judged on the same
 * maths the bits were drawn with. The maths is pure; `createCamera` adds a
 * clock for animated moves.
 */

import type { Camera, Unsub } from '../contracts';
import { DEFAULT_MOTION, lerp, progress, type Ease } from './tween';

export interface CameraState { readonly x: number; readonly y: number; readonly zoom: number; readonly tilt: number }  // tilt in degrees
export interface Rect { readonly left: number; readonly top: number; readonly width: number; readonly height: number }
/** Where things are on screen, in viewport CSS px: the whole canvas, and the stage inside it. */
export interface Viewport { readonly canvas: Rect; readonly stage: Rect }

export const ZOOM_MIN = 0.08;
export const ZOOM_MAX = 8;
export const TILT_MAX = 30;

/** Perspective distance for a stage viewport: far enough that a 7° lean reads as depth, not distortion. */
export const perspective = (vp: Viewport): number => Math.max(1000, 1.6 * vp.stage.height);

const trig = (tilt: number) => {
  const r = (tilt * Math.PI) / 180;
  return { c: Math.cos(r), s: Math.sin(r) };
};

/** Stage units → viewport CSS px. */
export const project = (cam: CameraState, vp: Viewport, sx: number, sy: number): { x: number; y: number } => {
  const D = perspective(vp);
  const { c, s } = trig(cam.tilt);
  const vx = (sx - cam.x) * cam.zoom, vy = (sy - cam.y) * cam.zoom;
  const f = D / (D - s * vy);
  return { x: vp.stage.left + vp.stage.width / 2 + vx * f, y: vp.stage.top + vp.stage.height / 2 + vy * c * f };
};

/** Viewport CSS px → stage units: the ray through the pixel, met with the tilted plate. */
export const unproject = (cam: CameraState, vp: Viewport, px: number, py: number): { x: number; y: number } => {
  const D = perspective(vp);
  const { c, s } = trig(cam.tilt);
  const u = px - (vp.stage.left + vp.stage.width / 2);
  const w = py - (vp.stage.top + vp.stage.height / 2);
  const den = c * D + w * s;
  // Above the horizon the ray never meets the plate; answer the far edge rather than infinity.
  const vy = Math.abs(den) < 1e-9 ? -D / Math.max(s, 1e-9) : (w * D) / den;
  const f = D / (D - s * vy);
  const vx = u / f;
  return { x: cam.x + vx / cam.zoom, y: cam.y + vy / cam.zoom };
};

/**
 * The same projection as a column-major mat4 from stage (x, y, 0, 1) to clip
 * space of the canvas; the perspective divide lands on `project`'s pixel.
 */
export const matrix = (cam: CameraState, vp: Viewport, out: Float32Array = new Float32Array(16), offset = 0): Float32Array => {
  const D = perspective(vp);
  const { c, s } = trig(cam.tilt);
  const W = Math.max(1, vp.canvas.width), H = Math.max(1, vp.canvas.height);
  const cx = vp.stage.left - vp.canvas.left + vp.stage.width / 2;
  const cy = vp.stage.top - vp.canvas.top + vp.stage.height / 2;
  const kx = (2 * cx) / W - 1, ky = 1 - (2 * cy) / H;
  const z = cam.zoom;
  // clip.w = D - s·z·(sy - y)
  const d0 = 0, d1 = -s * z, d3 = D + s * z * cam.y;
  // clip.x = (2D z / W)(sx - x) + kx·clip.w
  const ax = (2 * D * z) / W;
  const a0 = ax, a1 = kx * d1, a3 = -ax * cam.x + kx * d3;
  // clip.y = -(2 D c z / H)(sy - y) + ky·clip.w
  const by = (2 * D * c * z) / H;
  const b0 = 0, b1 = -by + ky * d1, b3 = by * cam.y + ky * d3;
  out.fill(0, offset, offset + 16);
  out[offset + 0] = a0; out[offset + 1] = b0; out[offset + 3] = d0;
  out[offset + 4] = a1; out[offset + 5] = b1; out[offset + 7] = d1;
  out[offset + 12] = a3; out[offset + 13] = b3; out[offset + 15] = d3;
  return out;
};

/** Apply a column-major mat4 to (x, y, 0, 1) and land on the canvas in viewport px (for tests and Canvas2D). */
export const applyMatrix = (m: ArrayLike<number>, vp: Viewport, x: number, y: number, offset = 0): { x: number; y: number; w: number } => {
  const cx = (m[offset + 0] as number) * x + (m[offset + 4] as number) * y + (m[offset + 12] as number);
  const cy = (m[offset + 1] as number) * x + (m[offset + 5] as number) * y + (m[offset + 13] as number);
  const cw = (m[offset + 3] as number) * x + (m[offset + 7] as number) * y + (m[offset + 15] as number);
  const nx = cx / cw, ny = cy / cw;
  return { x: vp.canvas.left + ((nx + 1) / 2) * vp.canvas.width, y: vp.canvas.top + ((1 - ny) / 2) * vp.canvas.height, w: cw };
};

/** The camera a fit lands on: `rect` centred, as large as fits with `pad` px around it. */
export const fitState = (rect: { x: number; y: number; w: number; h: number }, vp: Viewport, tilt: number, pad = 32): CameraState => {
  const zw = (vp.stage.width - 2 * pad) / Math.max(1, rect.w);
  const zh = (vp.stage.height - 2 * pad) / Math.max(1, rect.h);
  const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.min(zw, zh)));
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2, zoom, tilt };
};

// ---------- the packed camera the renderers read ----------

/** Layout of FrameModel.camera. */
export const CAM = {
  matrix: 0,          // 16 floats, stage → clip
  canvasW: 16, canvasH: 17, dpr: 18, zoom: 19,
  canvasLeft: 20, canvasTop: 21,
  parallaxX: 22, parallaxY: 23,   // backdrop offset, CSS px
  time: 24,                       // seconds, the backdrop's clock
  tilt: 25,                       // radians
  reduced: 26,
  length: 28,
} as const;

// ---------- the live camera ----------

export interface CameraImpl extends Camera {
  setViewport(vp: Viewport): void;
  viewport(): Viewport;
  /** Advance an animated move; true while one is running. */
  tick(now: number): boolean;
  animating(): boolean;
  /** Tilt off (reduced motion, or the degrade ladder's fourth rung): the plate lies flat. */
  setTiltAllowed(on: boolean): void;
  setReduced(on: boolean): void;
  /**
   * Told just before an animated move lands at once because motion is
   * reduced (a dive becomes a cut): the view on screen is still the old one,
   * so fx can hold it and crossfade to the new one in 150 ms (§4.6).
   */
  onCut(fn: () => void): Unsub;
  /** Write the matrix and viewport into a FrameModel.camera array. */
  pack(out: Float32Array, dpr: number): void;
}

/** A move that at least doubles or halves the zoom is a dive: 900 ms on the dive curve, not a 420 ms travel. */
export const isDive = (fromZoom: number, toZoom: number): boolean => Math.abs(Math.log(toZoom / Math.max(1e-6, fromZoom))) >= Math.log(2) - 1e-9;

export const createCamera = (opts: { now?: () => number; duration?: number; ease?: Ease; diveDuration?: number; diveEase?: Ease } = {}): CameraImpl => {
  const now = opts.now ?? (() => performance.now());
  const duration = opts.duration ?? DEFAULT_MOTION.travel;
  const diveDuration = opts.diveDuration ?? DEFAULT_MOTION.dive;
  let ease = opts.ease ?? DEFAULT_MOTION.out;
  let target: { x: number; y: number; zoom: number; tilt: number } = { x: 0, y: 0, zoom: 1, tilt: 0 };
  let from = { ...target };
  let cur = { ...target };
  let t0 = 0, dur = 0;
  let tiltAllowed = true, reduced = false;
  let vp: Viewport = { canvas: { left: 0, top: 0, width: 1, height: 1 }, stage: { left: 0, top: 0, width: 1, height: 1 } };
  const fns = new Set<() => void>();
  const cutFns = new Set<() => void>();
  const emit = () => { for (const fn of fns) fn(); };

  const effective = (): CameraState => ({ x: cur.x, y: cur.y, zoom: cur.zoom, tilt: tiltAllowed && !reduced ? cur.tilt : 0 });

  const clampState = (v: { x: number; y: number; zoom: number; tilt: number }) => ({
    x: Number.isFinite(v.x) ? v.x : 0,
    y: Number.isFinite(v.y) ? v.y : 0,
    zoom: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number.isFinite(v.zoom) ? v.zoom : 1)),
    tilt: Math.min(TILT_MAX, Math.max(0, Number.isFinite(v.tilt) ? v.tilt : 0)),
  });

  const moveTo = (next: { x: number; y: number; zoom: number; tilt: number }, animate: boolean) => {
    const to = clampState(next);
    const moves = to.x !== cur.x || to.y !== cur.y || to.zoom !== cur.zoom || to.tilt !== cur.tilt;
    if (animate && reduced && moves) for (const fn of cutFns) fn();
    target = to;
    if (animate && !reduced) {
      from = { ...cur };
      t0 = now();
      const dive = isDive(cur.zoom, target.zoom);
      dur = dive ? diveDuration : duration;
      ease = dive ? opts.diveEase ?? DEFAULT_MOTION.diveEase : opts.ease ?? DEFAULT_MOTION.out;
    } else {
      cur = { ...target };
      dur = 0;
    }
    emit();
  };

  const cam: CameraImpl = {
    get: () => effective(),
    set(v, animate = false) {
      moveTo({ ...target, ...v }, animate);
    },
    fit(rect, animate = false) {
      moveTo(fitState(rect, vp, target.tilt), animate);
    },
    toScreen: (x, y) => project(effective(), vp, x, y),
    toStage: (x, y) => unproject(effective(), vp, x, y),
    on(fn): Unsub {
      fns.add(fn);
      return () => fns.delete(fn);
    },
    onCut(fn): Unsub {
      cutFns.add(fn);
      return () => cutFns.delete(fn);
    },
    setViewport(next) {
      vp = next;
      emit();
    },
    viewport: () => vp,
    tick(t) {
      if (dur <= 0) return false;
      const p = progress(t, t0, dur);
      const e = ease(p);
      cur = {
        x: lerp(from.x, target.x, e), y: lerp(from.y, target.y, e),
        // zoom moves in log space so a dive feels even
        zoom: Math.exp(lerp(Math.log(from.zoom), Math.log(target.zoom), e)),
        tilt: lerp(from.tilt, target.tilt, e),
      };
      if (p >= 1) { cur = { ...target }; dur = 0; }
      emit();
      return dur > 0;
    },
    animating: () => dur > 0,
    setTiltAllowed(on) {
      if (tiltAllowed === on) return;
      tiltAllowed = on;
      emit();
    },
    setReduced(on) {
      reduced = on;
      if (on && dur > 0) { cur = { ...target }; dur = 0; }
      emit();
    },
    pack(out, dpr) {
      const e = effective();
      matrix(e, vp, out, CAM.matrix);
      out[CAM.canvasW] = vp.canvas.width;
      out[CAM.canvasH] = vp.canvas.height;
      out[CAM.dpr] = dpr;
      out[CAM.zoom] = e.zoom;
      out[CAM.canvasLeft] = vp.canvas.left;
      out[CAM.canvasTop] = vp.canvas.top;
      out[CAM.tilt] = (e.tilt * Math.PI) / 180;
    },
  };
  return cam;
};
